
import { computed, defineComponent, PropType, ref, Ref, StyleValue } from 'vue';
import breakpointsState from '@/core/responsive/breakpoints/breakpoints-state.observable';
import { Swiper } from 'swiper/vue';
import SwiperCore, { Pagination, Navigation, A11y, Keyboard, SwiperOptions, Thumbs, Zoom, Swiper as SwiperInterface } from 'swiper';
import dictionaryService from '@/core/dictionary/dictionary.service';

SwiperCore.use([Pagination, Navigation, A11y, Keyboard, Thumbs, Zoom]);

export default defineComponent({
    name: 'Swiper',
    components: {
        SwiperJS: Swiper,
    },
    props: {
        freeMode: {
            type: [Boolean, String],
            required: false,
            default: 'max-sm',
        },
        options: {
            type: Object as PropType<Partial<SwiperOptions>>,
            required: false,
            default: () => {},
        },
        navPrevLink: {
            type: String,
            required: false,
            default: dictionaryService.get('global/generic/swiperNavigatePrevious'),
        },
        navNextLink: {
            type: String,
            required: false,
            default: dictionaryService.get('global/generic/swiperNavigateNext'),
        },
        thumbs: {
            type: SwiperInterface,
            required: false,
            default: null,
        },
        hideArrows: {
            type: Boolean,
            required: false,
            default: false,
        },
        navStyle: {
            type: Object as Partial<StyleValue>,
            required: false,
            default: () => {},
        },
    },
    emits: {
        init: (swiper: SwiperCore) => true,
        change: (swiper: SwiperCore) => true,
        transitionStart: (swiper: SwiperCore) => true,
        transitionEnd: (swiper: SwiperCore) => true,
        touchEnd: (swiper: SwiperCore) => true,
        zoomChange: (swiper: SwiperCore, scale, imageEl, slideEl) => ({ swiper, scale, imageEl, slideEl }),
    },
    setup(props, { emit }) {
        const swiperCtrl = ref<SwiperCore | undefined>();
        const isEnd = ref(false);
        const isBeginning = ref(true);

        const swiperOptions: Ref<Partial<SwiperOptions>> = computed(() => ({
            a11y: {
                nextSlideMessage: props.navNextLink,
                prevSlideMessage: props.navPrevLink,
            },
            slidesPerView: 'auto',
            spaceBetween: 10,
            pagination: {
                dynamicBullets: true,
                clickable: true,
            },
            keyboard: {
                enabled: true,
                onlyInViewport: true,
                pageUpDown: false,
            },
            freeMode: freeMode.value,
            centeredSlides: !freeMode.value,
            centeredSlidesBounds: !freeMode.value,
            navigation: freeMode.value || props.hideArrows ? false : {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            thumbs: {
                swiper: props.thumbs,
            },
            ...props.options,
        }));

        const freeMode: Ref<boolean> = computed(() => {
            const isBreakPointKey = typeof props.freeMode === 'string';
            if (isBreakPointKey) {
                return breakpointsState.isBreakpointActive(props.freeMode as string);
            } else {
                return props.freeMode as boolean;
            }
        });

        const eventHandlers = computed(() => {
            const handlers = {
                swiper: (swiper: SwiperCore) => {
                    isEnd.value = swiper.isEnd;
                    isBeginning.value = swiper.isBeginning;
                    swiperCtrl.value = swiper;
                    emit('init', swiper);
                },
                slideChange: (swiper: SwiperCore) => {
                    isEnd.value = swiper.isEnd;
                    isBeginning.value = swiper.isBeginning;
                    emit('change', swiper);
                },
                slideChangeTransitionStart: (swiper: SwiperCore) => emit('transitionStart', swiper),
                slideChangeTransitionEnd: (swiper: SwiperCore) => emit('transitionEnd', swiper),
                zoomChange: (swiper: SwiperCore, scale, imageEl, slideEl) => emit('zoomChange', { swiper, scale, imageEl, slideEl }),
                touchEnd: (swiper: SwiperCore, scale) => emit('touchEnd', { swiper, scale }),
            };
            return handlers;
        });

        const showDummySlideForMiscalculatedSlidesPerGroup = computed(() => {
            if (!swiperCtrl.value) {
                return false;
            }
            const groupCount = swiperOptions.value?.slidesPerGroup ?? 1;
            const slidesCount = swiperCtrl.value?.slides.length ?? 0;
            const isGroup = groupCount > 1;
            return isGroup && slidesCount > groupCount;
        });

        return {
            isBeginning,
            isEnd,
            swiperCtrl,
            swiperOptions,
            eventHandlers,
            showDummySlideForMiscalculatedSlidesPerGroup,
        };
    },
});
